import { useCallback } from 'react';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from 'src/hooks/useApiClient';
import type {
	Quotation,
	QuotationFilters,
	QuotationListRequest,
	QuotationListResponse,
	SaveQuotationBody,
} from './types';

export const QUOTATIONS_QUERY_KEY = 'quotations';

const EXPORT_PAGE_SIZE = 100;

export const toListRequest = (
	filters: QuotationFilters
): QuotationListRequest => ({
	q: filters.search || undefined,
	dealer: filters.dealer || undefined,
	createdAtFrom: filters.createdAtFrom
		? dayjs(filters.createdAtFrom).startOf('day').toISOString()
		: undefined,
	createdAtTo: filters.createdAtTo
		? dayjs(filters.createdAtTo).endOf('day').toISOString()
		: undefined,
	sortBy: 'createdAt',
	sortDirection: 'desc',
});

export const useQuotations = (
	filters: QuotationFilters,
	page: number,
	pageSize: number
) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [QUOTATIONS_QUERY_KEY, filters, page, pageSize],
		queryFn: (): Promise<QuotationListResponse> =>
			apiClient.motornetControllerService.getQuotations({
				...toListRequest(filters),
				page,
				pageSize,
			}),
		staleTime: 0,
		gcTime: 0,
		retry: false,
		refetchOnWindowFocus: false,
	});
};

export const useFetchAllQuotations = () => {
	const { apiClient } = useApiClient();

	return useCallback(
		async (
			filters: QuotationFilters,
			onProgress?: (loaded: number, total: number) => void
		): Promise<Quotation[]> => {
			const all: Quotation[] = [];
			for (let page = 1; ; page++) {
				const res = await apiClient.motornetControllerService.getQuotations({
					...toListRequest(filters),
					page,
					pageSize: EXPORT_PAGE_SIZE,
				});
				all.push(...res.items);
				onProgress?.(all.length, res.total);
				if (res.items.length === 0 || all.length >= res.total) return all;
			}
		},
		[apiClient]
	);
};

export const useSaveQuotation = () => {
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: SaveQuotationBody) =>
			apiClient.motornetControllerService.saveQuotation(body),
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: [QUOTATIONS_QUERY_KEY] }),
	});
};
